import { useCallback, useState } from 'react';
import { ipadHochQuery, useMediaQuery } from '../lib/useUiPreferences';
import { LeistenSymbol } from './KlappKnopf';

/*
 * GUIDE UND BUEHNE ALS ZWEI REITER — iPad hochkant (Rafael, 03.10.2026 11:15):
 * „Am iPad hochkant oben zwei Reiter Guide und Bühne; ein Tipp wechselt, jede Ansicht
 * bekommt die volle Breite. Querformat und Desktop bleiben unverändert."
 *
 * Vorher legte sich die Guide-Spalte unter 1100 px als 390 px breite Ueberlagerung
 * (position:absolute) ueber die Buehne. Hochkant ist fuer zwei Spalten kein Platz,
 * deshalb hier ein Entweder-Oder. Die Wahl ist GERAETE-Zustand (localStorage),
 * sie schreibt nichts an den Server — weder die Guide-Anordnung noch Buehnen-Layouts.
 */
export type GuideBuehneReiterWahl = 'guide' | 'buehne';
const MERKER = 'cui-ipad-reiter';
/** Schmal, Touch, hochkant: dieselbe 1100-px-Grenze wie die alte Ueberlagerung (responsive.css). */
export const IPAD_HOCH_ABFRAGE = ipadHochQuery;

/*
 * REITER ODER NEBENEINANDER — ein Klick (Rafael, 03.10.2026 12:50): „zusätzlich einen Modus
 * nebeneinander: links der Guide (kompakt), rechts die Bühne". Gemerkt je Geraet UND je Lage
 * (hochkant / breit): eine Wahl hochkant soll das Querformat nicht umstellen, wo „nebeneinander"
 * die Vorgabe ist. Hochkant ist die Vorgabe „Reiter". Nichts davon geht an den Server.
 */
export type GuideAnsicht = 'reiter' | 'nebeneinander';
const ANSICHT_MERKER = 'cui-guide-ansicht';

function lies(): GuideBuehneReiterWahl {
  try { return localStorage.getItem(MERKER) === 'buehne' ? 'buehne' : 'guide'; } catch { return 'guide'; }
}
function liesAnsicht(hochkant: boolean): GuideAnsicht | null {
  try { const v = localStorage.getItem(`${ANSICHT_MERKER}:${hochkant ? 'hoch' : 'breit'}`); return v === 'reiter' || v === 'nebeneinander' ? v : null; } catch { return null; }
}

export function useGuideBuehneReiter() {
  const hochkant = useMediaQuery(IPAD_HOCH_ABFRAGE);
  const [wahl, setWahlState] = useState<GuideBuehneReiterWahl>(lies);
  const setWahl = useCallback((w: GuideBuehneReiterWahl) => {
    setWahlState(w);
    try { localStorage.setItem(MERKER, w); } catch { /* Wahl gilt dann nur bis zum Neuladen. */ }
  }, []);
  const [gewaehlt, setGewaehlt] = useState<Record<string, GuideAnsicht>>({});
  const lage = hochkant ? 'hoch' : 'breit';
  const ansicht: GuideAnsicht = gewaehlt[lage] ?? liesAnsicht(hochkant) ?? (hochkant ? 'reiter' : 'nebeneinander');
  const setAnsicht = useCallback((a: GuideAnsicht) => {
    setGewaehlt(g => ({ ...g, [lage]: a }));
    try { localStorage.setItem(`${ANSICHT_MERKER}:${lage}`, a); } catch { /* gilt bis zum Neuladen */ }
  }, [lage]);
  return { hochkant, wahl, setWahl, ansicht, setAnsicht };
}

/** Der dritte Knopf: wechselt mit einem Klick zwischen „Reiter" und „nebeneinander". */
export function GuideAnsichtKnopf({ ansicht, onAnsicht, senkrecht }: { ansicht: GuideAnsicht; onAnsicht: (a: GuideAnsicht) => void; senkrecht?: boolean }) {
  const ziel: GuideAnsicht = ansicht === 'reiter' ? 'nebeneinander' : 'reiter';
  const titel = ziel === 'nebeneinander' ? 'Guide und Bühne nebeneinander zeigen' : 'Guide und Bühne einzeln zeigen — abwechselnd, jede in voller Breite (oben umschalten)';
  return <button type="button" className={`cui-guide-ansicht-knopf${senkrecht ? ' senkrecht' : ''}`} data-ai-id={`guide-ansicht-${ziel}`}
    aria-pressed={false} title={titel} aria-label={titel} onClick={() => onAnsicht(ziel)}>
    {/* „Reiter" war Innensprache (03.10.2026 17:16): „Einzeln" = Guide und Bühne abwechselnd, je in voller Breite. */}
    <LeistenSymbol art={ziel} /><span>{ziel === 'nebeneinander' ? 'Nebeneinander' : 'Einzeln'}</span>
  </button>;
}

export default function GuideBuehneReiter({ wahl, onWahl, ansicht, onAnsicht }: { wahl: GuideBuehneReiterWahl; onWahl: (w: GuideBuehneReiterWahl) => void; ansicht: GuideAnsicht; onAnsicht: (a: GuideAnsicht) => void }) {
  const reiter: Array<[GuideBuehneReiterWahl, string]> = [['guide', 'Guide'], ['buehne', 'Bühne']];
  return <div className="cui-ipad-reiter">
    <div role="tablist" aria-label="Guide oder Bühne" className="cui-ipad-reiter-liste">
      {reiter.map(([id, text]) => (
        <button key={id} type="button" role="tab" data-ai-id={`ipad-reiter-${id}`}
          aria-selected={wahl === id} aria-controls={id === 'guide' ? 'guide-dock-content' : 'guide-stage-area'}
          onClick={() => onWahl(id)}>{text}</button>
      ))}
    </div>
    <GuideAnsichtKnopf ansicht={ansicht} onAnsicht={onAnsicht} />
  </div>;
}
